import Swiper, {Keyboard, Mousewheel, Navigation, Parallax} from 'swiper';
import CircleSlider from '@/components/circle-slider';
import DesignSlider from '@/components/design-slider';
import {Lethargy} from 'lethargy/lethargy.js';
import Observer from '@/common/scripts/observer';
import {throttle} from 'throttle-debounce';
import {Utils} from '@/common/scripts/utils';

const observer = new Observer();
const MOUSE_SCROLL_DELTA = 100;

class FullScreenSlider {
    // eslint-disable-next-line max-statements
    constructor(wrapper) {
        this.slider = null;
        this.CircleSlider = CircleSlider;
        this.DesignSlider = DesignSlider;

        /**
         * Враппер страницы
         * @type {HTMLElement}
         */
        this.sliderWrapper = wrapper;
        this.nextSlideButtons = [...this.sliderWrapper.querySelectorAll('.j-full-screen-slider__next')];
        this.header = document.querySelector('.j-header');
        this.footer = document.querySelector('footer');
        this.speed = 1500;
        this.activeClass = 'is-active';
        this.touchedClass = 'is-touched';
        this.scrollValues = ['auto', 'scroll', 'overlay'];
        this.wheelDeltaCount = 0;
        this.isDesktop = !Utils.isMobile();
        this.touchEventDetected = false;
        this.throttleListener = throttle(300, this._throttleListener.bind(this));
        this.touchListener = this._touchListener.bind(this);
        this.slidersMap = new Map();
        this._autoMode = true;
        this._innerSliderActive = false;
        this._lethargy = new Lethargy(8, 50);

        this._init();
    }

    /**
     * Получаем экземпляр дочернего слайдера по DOM-элементу в качестве ключа
     */
    get currentSliderInstance() {
        const currentSlide = this.slider.slides[this.slider.activeIndex];

        return this.slidersMap.get(currentSlide);
    }

    get isAutoModeEnabled() {
        return this._autoMode;
    }

    enableAutoMode() {
        this._autoMode = true;
    }

    disableAutoMode() {
        this._autoMode = false;
    }

    goNextSlide() {
        this.slider.slideNext(this.speed);
    }

    goPrevSlide() {
        this.slider.slidePrev(this.speed);
    }

    pauseScroll() {
        if (this.slider) {
            this.slider.mousewheel.disable();
            this.slider.allowTouchMove = false;
        }
    }

    unpauseScroll() {
        if (this.slider) {
            this.slider.mousewheel.enable();
            this.slider.allowTouchMove = true;
        }
    }

    pauseTouch() {
        if (this.slider) {
            this.slider.allowTouchMove = false;
        }
    }

    unpauseTouch() {
        if (this.slider) {
            this.slider.allowTouchMove = true;
        }
    }

    pauseKeyboard() {
        if (this.slider) {
            this.slider.keyboard.disable();
        }
    }

    unpauseKeyboard() {
        if (this.slider) {
            this.slider.keyboard.enable();
        }
    }

    enableInnerSliderFlag() {
        this._innerSliderActive = true;
    }

    disableInnerSliderFlag() {
        this._innerSliderActive = false;
    }

    _init() {
        if (!this.isDesktop) {
            this.footer.classList.remove('is-main');
            this._setIndependentSliders();

            return;
        }

        this._subscribes();
        this._bindEvents();
        this._initSwiper();
        this._setSlidersDependencies();
        this._bindEventsSwiper();

        window.addEventListener('wheel', (event) => {
            if (this._innerSliderActive) {
                return;
            }

            if (event.deltaY === MOUSE_SCROLL_DELTA || this._lethargy.check(event)) {
                this.unpauseScroll();
            } else {
                this.pauseScroll();
            }
        });
    }

    /**
     * Инициализирует независимые внутренние слайдеры, если Swiper нужно отключить
     * Требуется для мобильной версии
     * Позволяет обращатся к дочерним слайдера по DOM-элементу в качестве ключа
     */
    _setIndependentSliders() {
        const circleSliderNodes = document.querySelectorAll('.j-circle-slider');
        const designSliderNodes = document.querySelectorAll('.j-design-slider');

        circleSliderNodes.forEach((node) => {
            this.slidersMap.set(node, new this.CircleSlider(node, null));
        });

        designSliderNodes.forEach((node) => {
            this.slidersMap.set(node, new this.DesignSlider(node, null));
        });
    }

    /**
     * Устанавливает связь между текущим слайдом и нужным экземпляром слайдера.
     * Позволяет обращаться к дочерним слайдерам по DOM-элементу в качестве ключа
     */
    _setSlidersDependencies() {
        this.slider.slides.forEach((slide) => {
            // eslint-disable-next-line consistent-this
            let slider = this;
            const circleSliderNode = slide.querySelector('.j-circle-slider');
            const designSliderNode = slide.querySelector('.j-design-slider');

            if (circleSliderNode) {
                slider = new this.CircleSlider(circleSliderNode, this);
            } else if (designSliderNode) {
                slider = new this.DesignSlider(designSliderNode, this);
            }

            this.slidersMap.set(slide, slider);
        });
    }

    _subscribes() {
        observer.subscribe('preloader:finished', () => {
            if (this.slider) {
                this.slider.enabled = true;
            }
        });
    }

    _bindEvents() {
        const events = ['resize', 'orientationchange'];

        events.forEach((event) => {
            window.addEventListener(event, this._setSwiperOptions.bind(this), {passive: true});
        });
    }

    _bindEventsSwiper() {
        this.nextSlideButtons.forEach((button) => {
            button.addEventListener('click', () => {
                this.currentSliderInstance.goNextSlide(this.speed);
            });
        });
    }

    _setSwiperOptions() {
        this.isDesktop = !Utils.isMobile();

        if (this.slider) {
            this.slider.params.virtualTranslate = this.isDesktop;
            this.slider.virtualTranslate = this.isDesktop;
            this.slider.update();
        }
    }

    // eslint-disable-next-line max-lines-per-function
    _initSwiper() {
        const options = {
            modules                       : [Navigation, Mousewheel, Keyboard, Parallax],
            wrapperClass                  : 'j-full-screen-slider__wrapper',
            slideClass                    : 'j-full-screen-slider__slide',
            slidePrevClass                : 'is-prev',
            slideActiveClass              : 'is-active',
            slideNextClass                : 'is-next',
            direction                     : 'vertical',
            slidesPerView                 : 'auto',
            virtualTranslate              : this.isDesktop,
            parallax                      : true,
            speed                         : this.speed,
            followFinger                  : false,
            threshold                     : 20,
            enabled                       : false,
            // оставлено для удобства тестирования
            // initialSlide                  : 2,
            shortSwipes                   : true,
            longSwipes                    : false,
            preventInteractionOnTransition: true,
            mousewheel                    : true,
            keyboard                      : {
                enabled       : true,
                onlyInViewport: false
            },
            on: {
                init: (swiper) => {
                    this._initSwiperDetails(swiper);
                },
                activeIndexChange: (swiper) => {
                    const method = swiper.previousIndex > swiper.activeIndex ? 'add' : 'remove';

                    [...swiper.slides].forEach((slide) => {
                        slide.classList[method]('is-slide-up');
                    });
                },
                slideChange: (swiper) => {
                    this._onChange(swiper);
                },
                slideChangeTransitionStart: (swiper) => {
                    swiper.wrapperEl.classList.add('is-process');

                    const currentSlide = swiper.slides[swiper.activeIndex];
                    const currentSlideOverflow = window.getComputedStyle(currentSlide)['overflow-y'];

                    if (!this.touchEventDetected &&
                        currentSlide.scrollTop > 0 &&
                        this.scrollValues.includes(currentSlideOverflow)) {
                        setTimeout(() => {
                            currentSlide.scrollTo({
                                top     : 0,
                                behavior: 'smooth'
                            });
                        }, 700);
                    }

                    if (currentSlide?.dataset?.mode) {
                        this._activateInnerSlider(currentSlide);
                        this.enableInnerSliderFlag();
                    }
                },
                slideChangeTransitionEnd: (swiper) => {
                    swiper.wrapperEl.classList.remove('is-process');
                    swiper.el.classList.remove('is-process', 'home-slider_footer_theme-change');

                    const currentSlide = swiper.slides[swiper.activeIndex];
                    const currentSlideOverflow = window.getComputedStyle(currentSlide)['overflow-y'];

                    if (!this.scrollValues.includes(currentSlideOverflow)) {
                        return;
                    }

                    this.wheelDeltaCount = 0;
                    this._lockAfterScrollPage(currentSlide);
                    this._lockTouchScroll(currentSlide);
                },
                keyPress: () => {
                    if (this.currentSliderInstance === this) {
                        this.unpauseScroll();
                    }
                }
            }
        };

        this.slider = new Swiper(this.sliderWrapper, options);
    }

    _initSwiperDetails(swiper) {
        const footer = this.footer.cloneNode(true);

        footer.classList.remove('is-main');
        this._setHeaderColor(swiper.slides[swiper.activeIndex].dataset.headerTheme);
        this._setSlideModify(swiper);
        this._setModify(swiper, 'buttonTheme', 'button_theme');
        this._setModify(swiper, 'footerTheme', 'footer_theme');
        swiper.slides[swiper.slides.length - 1].querySelector('.cards__container')?.appendChild(footer);
    }

    _onChange(swiper) {
        swiper.el.classList.add('is-process');
        this._setHeaderColor(swiper.slides[swiper.activeIndex].dataset.headerTheme);
        this._setSlideModify(swiper);
        this._setModify(swiper, 'buttonTheme', 'button_theme', true);
        this._setModify(swiper, 'footerTheme', 'footer_theme', true);
    }

    /**
     *
     * @param {string} color - цвет шапки активного слайда
     */
    _setHeaderColor(color = '') {
        if (!this.header) {
            return;
        }

        const nowColor = this.header.dataset.activeColor;

        if (!color) {
            if (nowColor !== '') {
                this.header.classList.remove(`header_theme_${nowColor}`);
                this.header.dataset.activeColor = '';
            }

            return;
        }

        if (nowColor !== '') {
            this.header.classList.remove(`header_theme_${nowColor}`);
            this.header.dataset.activeColor = color;
        }

        this.header.classList.add(`header_theme_${color}`);
        this.header.dataset.activeColor = color;
    }

    /**
     * Устанавливает модификатор смены вида футера
     * @param {Object} swiper - объект слайдера
     * @param {String} attributeName - имя дата-атрибута у слайда
     * @param {String} suffix - суффикс имени класса модификатора
     * @param {Boolean} setChangeSlide - надо ли устанавливать класс смены темы
     * @private
     */
    _setModify(swiper, attributeName, suffix, setChangeSlide = false) {
        const activeSlide = swiper.slides[swiper.activeIndex];
        const modify = activeSlide.dataset[attributeName] || 'default';

        [...swiper.el.classList].filter((className) => {
            return className.includes(suffix);
        }).forEach((classToRemove) => {
            if (setChangeSlide) {
                const method = `home-slider_${suffix}-${modify}` === classToRemove ? 'remove' : 'add';

                swiper.el.classList[method](`home-slider_${suffix}-change`);
            }
            swiper.el.classList.remove(classToRemove);
        });

        swiper.el.classList.add(`home-slider_${suffix}-${modify}`);
    }

    _setSlideModify(swiper) {
        [...swiper.el.classList].filter((className) => {
            return className.includes('is-n-');
        }).forEach((classToRemove) => {
            swiper.el.classList.remove(classToRemove);
        });
        swiper.el.classList.add(`is-n-${swiper.activeIndex}`);
    }

    /**
     * Переключает управление на внутренний слайдер
     * @param {HTMLElement} activeSlide - активный слайд слайдера
     * @private
     */
    _activateInnerSlider(activeSlide) {
        if (activeSlide.classList.contains(this.touchedClass) || !this.isDesktop) {
            return;
        }

        this.enableInnerSliderFlag();
        this.pauseScroll();
        this.currentSliderInstance?.activateAsInnerSlider(activeSlide);
    }

    _lockAfterScrollPage(currentSlide) {
        this.enableInnerSliderFlag();
        this.pauseScroll();
        this.pauseTouch();
        currentSlide.addEventListener('wheel', this.throttleListener, {passive: true});
    }

    _unlockAfterScrollPage(currentSlide) {
        this.disableInnerSliderFlag();
        this.unpauseScroll();
        currentSlide.removeEventListener('wheel', this.throttleListener);
    }

    _lockTouchScroll(currentSlide) {
        this.pauseTouch();
        currentSlide.addEventListener('touchend', this.touchListener);
    }

    _unlockTouchScroll(currentSlide) {
        this.unpauseTouch();
        currentSlide.removeEventListener('touchend', this.touchListener);
    }

    _throttleListener(event) {
        if (!this.isDesktop) {
            return;
        }

        const currentSlide = this.slider.slides[this.slider.activeIndex];
        const direction = event.deltaY > 0 ? 'down' : 'up';

        if (direction === 'up') {
            if (currentSlide.scrollTop === 0) {
                if ((this.wheelDeltaCount >= -5 && this.wheelDeltaCount < 0) ||
                    this.wheelDeltaCount <= -MOUSE_SCROLL_DELTA ||
                    this._lethargy.check(event)
                ) {
                    this._unlockAfterScrollPage(currentSlide);
                } else {
                    this.wheelDeltaCount = this.wheelDeltaCount + event.deltaY;
                }
            } else {
                this.wheelDeltaCount = 0;
            }
        } else if (Math.ceil(currentSlide.scrollTop + currentSlide.offsetHeight) >= currentSlide.scrollHeight) {
            if ((this.wheelDeltaCount > 0 && this.wheelDeltaCount <= 5) ||
                this.wheelDeltaCount >= MOUSE_SCROLL_DELTA ||
                this._lethargy.check(event)
            ) {
                this._unlockAfterScrollPage(currentSlide);
            } else {
                this.wheelDeltaCount = this.wheelDeltaCount + event.deltaY;
            }
        } else {
            this.wheelDeltaCount = 0;
        }
    }

    _touchListener() {
        this.touchEventDetected = true;

        const currentSlide = this.slider.slides[this.slider.activeIndex];
        const diff = currentSlide.scrollHeight - currentSlide.clientHeight;

        if (currentSlide.scrollTop <= 0) {
            this.goPrevSlide();
            this._unlockTouchScroll(currentSlide);
        } else if (currentSlide.scrollTop >= diff) {
            this.goNextSlide();
            this._unlockTouchScroll(currentSlide);
        }
    }
}

export default FullScreenSlider;
